import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, CheckCircle, Copy, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventPay() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isSuccess = sp.get('success') === 'true';

  const [ev, setEv] = useState(null);
  const [step, setStep] = useState('loading');
  const [name, setName] = useState(sp.get('nome') || '');
  const [type, setType] = useState(sp.get('tipo') === 'convidado' ? 'convidado' : 'aluno');
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/paid-events/${id}`).then(({ data }) => {
      setEv(data); setStep(isSuccess ? 'checking' : 'form');
    }).catch(() => setStep('notfound'));
  }, [id, isSuccess]);

  useEffect(() => {
    const sid = sp.get('session_id'); const pid = sp.get('pid');
    if (!isSuccess || !sid || !pid) return;
    let n = 0;
    const chk = async () => {
      n += 1;
      try {
        const { data } = await axios.get(`${API}/paid-events/${id}/checkout-status/${pid}/${sid}`);
        if (data.payment_status === 'paid') { setStep('success'); return; }
      } catch { /* retry */ }
      if (n < 6) setTimeout(chk, 1500); else setStep('success');
    };
    chk();
    /* eslint-disable-next-line */
  }, [isSuccess]);

  const price = ev ? (type === 'convidado' ? ev.price_guest : ev.price_student) : 0;

  const payCard = async () => {
    if (!name.trim()) { toast.error('Digite seu nome'); return; }
    try {
      const { data } = await axios.post(`${API}/paid-events/${id}/checkout`, { payer_name: name.trim(), payer_type: type, origin_url: window.location.origin });
      if (data.url) {
        setCheckoutUrl(data.url); setStep('redirecting');
        setTimeout(() => { try { window.location.assign(data.url); } catch { /* manual */ } }, 700);
      }
    } catch { toast.error('Erro ao iniciar pagamento'); }
  };

  const onZelleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!name.trim()) { toast.error('Digite seu nome'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('payer_name', name.trim()); fd.append('payer_type', type);
      await axios.post(`${API}/paid-events/${id}/zelle-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante enviado!'); setStep('success');
    } catch { toast.error('Erro ao enviar comprovante'); }
    finally { setUploading(false); }
  };

  if (step === 'loading') return <div className="min-h-screen bg-[#0B1120] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin" /></div>;

  if (step === 'redirecting') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <div className="w-14 h-14 rounded-full border-4 border-[#E85D04] border-t-transparent animate-spin mb-6" />
        <h2 className="font-['Outfit'] text-xl font-bold text-[#1A1A1A]">Abrindo pagamento seguro...</h2>
        <p className="text-sm text-[#73706A] mt-2">Se a página não abrir, toque no botão abaixo.</p>
        {checkoutUrl && <a href={checkoutUrl} target="_self" rel="noopener" className="inline-flex items-center justify-center w-full max-w-sm bg-[#E85D04] text-white font-semibold py-4 rounded-lg text-lg mt-6" data-testid="event-open-checkout">Ir para o pagamento</a>}
        {checkoutUrl && <button onClick={() => { navigator.clipboard?.writeText(checkoutUrl); toast.success('Link copiado'); }} className="mt-3 text-sm text-[#0077B6] underline">Copiar link</button>}
        <p className="text-xs text-[#73706A] mt-4 max-w-sm">Pelo Instagram/Facebook? Abra no Safari ou Chrome para pagar.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#0B1120] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="event-pay-page">
        <div className="bg-[#1A1A1A] px-6 py-5 text-center">
          <p className="text-[#FFB703] text-xs uppercase tracking-widest font-semibold">Tia Karol Sports</p>
          <p className="text-white font-['Outfit'] text-lg font-bold mt-1">{ev?.name}</p>
        </div>

        {step === 'notfound' && <div className="p-8 text-center text-[#73706A]">Evento não encontrado.</div>}
        {step === 'checking' && <div className="p-8 text-center"><div className="w-12 h-12 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-[#73706A]">Confirmando pagamento...</p></div>}
        {step === 'success' && (
          <div className="p-8 text-center" data-testid="event-success">
            <div className="w-16 h-16 rounded-full bg-[#2D6A4F] flex items-center justify-center mx-auto mb-4"><CheckCircle size={40} weight="fill" className="text-white" /></div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Pagamento recebido!</h2>
            <p className="text-[#73706A] mt-2">Obrigado, {name}! Seu pagamento de ${price?.toFixed(2)} ({type}) foi registrado.</p>
          </div>
        )}

        {(step === 'form' || step === 'zelle') && (
          <div className="p-6">
            {ev?.description && <p className="text-[#73706A] text-sm text-center mb-4">{ev.description}</p>}
            {ev?.deadline && <p className="text-xs text-center text-[#B45309] mb-4">Preço promocional até {new Date(ev.deadline + 'T00:00:00').toLocaleDateString('pt-BR')}</p>}
            {ev?.closes_on && !ev.closed && <p className="text-xs text-center text-[#2D6A4F] font-semibold mb-4" data-testid="event-closes-on">Inscrições e pagamento até {new Date(ev.closes_on + 'T00:00:00').toLocaleDateString('pt-BR')}</p>}
            {ev?.closed && <p className="text-center font-semibold text-[#9B2226] bg-red-50 border border-red-200 rounded-lg py-3 mb-4" data-testid="event-closed-msg">Inscrições encerradas</p>}

            <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Seu nome</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Nome completo" className="w-full border-2 border-[#E6E2D8] rounded-lg px-3 py-3 mb-4 focus:border-[#E85D04] focus:outline-none" data-testid="event-name-input" />

            <div className="grid grid-cols-2 gap-2 mb-5">
              <button onClick={() => setType('aluno')} className={`py-3 rounded-lg font-semibold border-2 ${type === 'aluno' ? 'border-[#E85D04] bg-orange-50 text-[#E85D04]' : 'border-[#E6E2D8] text-[#73706A]'}`} data-testid="event-type-aluno">Aluno<br /><span className="text-xs">${ev?.price_student?.toFixed(2)}</span></button>
              <button onClick={() => setType('convidado')} className={`py-3 rounded-lg font-semibold border-2 ${type === 'convidado' ? 'border-[#E85D04] bg-orange-50 text-[#E85D04]' : 'border-[#E6E2D8] text-[#73706A]'}`} data-testid="event-type-convidado">Convidado<br /><span className="text-xs">${ev?.price_guest?.toFixed(2)}</span></button>
            </div>

            <div className="text-center mb-5">
              <p className="text-[#73706A] text-sm">Valor a pagar</p>
              <p className="font-['Outfit'] text-4xl font-bold text-[#1A1A1A]" data-testid="event-amount">${price?.toFixed(2)}</p>
            </div>

            {step === 'form' && !ev?.closed && (
              <div className="space-y-3">
                <button onClick={payCard} className="w-full flex items-center justify-center gap-2 bg-[#0077B6] text-white font-semibold py-4 rounded-xl" data-testid="event-pay-card"><CreditCard size={20} /> Pagar com Cartão</button>
                <button onClick={() => setStep('zelle')} className="w-full flex items-center justify-center gap-2 bg-white border-2 border-[#1A1A1A] text-[#1A1A1A] font-semibold py-4 rounded-xl" data-testid="event-pay-zelle">Pagar com Zelle</button>
              </div>
            )}
            {step === 'zelle' && (
              <div className="space-y-4" data-testid="event-zelle-box">
                <div className="bg-[#F5F2EB] rounded-xl p-4">
                  <p className="text-sm text-[#73706A]">Envie <strong className="text-[#1A1A1A]">${price?.toFixed(2)}</strong> via Zelle para:</p>
                  <div className="flex items-center justify-between mt-2 bg-white rounded-lg px-3 py-2">
                    <span className="font-mono font-semibold text-[#1A1A1A]">{ev?.zelle_phone}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(ev?.zelle_phone); toast.success('Copiado'); }} className="p-2 rounded-lg hover:bg-[#E6E2D8]"><Copy size={18} /></button>
                  </div>
                </div>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onZelleFile} data-testid="event-zelle-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-semibold py-4 rounded-xl disabled:opacity-50" data-testid="event-zelle-upload"><UploadSimple size={20} /> {uploading ? 'Enviando...' : 'Enviar comprovante Zelle'}</button>
                <button onClick={() => setStep('form')} className="w-full text-sm text-[#73706A]">Voltar</button>
              </div>
            )}
            <p className="text-center text-xs text-[#9CA3AF] mt-6">🔒 Pagamento seguro</p>
          </div>
        )}
      </div>
    </div>
  );
}
